import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { isValidWebsite } from "@/utils/domains";

export interface CandidateData {
  firstName?: string;
  lastName?: string;
  companyName?: string;
  email?: string;
  /** Set by the company enrichment-failure notification: /companies?new&website=… */
  website?: string;
}

const PREFILL_PARAMS = [
  "firstName",
  "lastName",
  "companyName",
  "email",
  "website"
] as const;

// URL-sourced values that can reach an href get shape-checked here, not per-caller.
const PARAM_VALIDATORS: Partial<
  Record<(typeof PREFILL_PARAMS)[number], (value: string) => boolean>
> = {
  website: isValidWebsite
};

/**
 * Detects a `?new` query param in the URL and calls `setOpen(true)`, then
 * removes the param so it doesn't persist in history. Used by list pages that
 * want to support deep-linking into their create modal (e.g. from a Dreamer
 * push notification: `/leads?new&firstName=...&lastName=...`).
 *
 * Returns any candidate pre-fill data that was embedded in the URL params.
 */
export function useOpenOnNew(setOpen: (open: boolean) => void): [CandidateData | null, () => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const [candidateData, setCandidateData] = useState<CandidateData | null>(null);

  useEffect(() => {
    if (searchParams.has("new")) {
      const prefill: CandidateData = {};
      PREFILL_PARAMS.forEach((param) => {
        const value = searchParams.get(param);
        if (!value) return;
        const isAcceptable = PARAM_VALIDATORS[param];
        if (isAcceptable && !isAcceptable(value)) return;
        prefill[param] = value;
      });

      if (Object.keys(prefill).length > 0) {
        setCandidateData(prefill);
      }

      setOpen(true);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete("new");
        PREFILL_PARAMS.forEach((param) => next.delete(param));
        return next;
      }, { replace: true });
    }
  }, [searchParams, setSearchParams, setOpen]);

  const clearCandidateData = () => setCandidateData(null);

  return [candidateData, clearCandidateData];
}
